import { monsterPhotos, monsterSheets, MONSTER_SHEET_SIZE, photoFocus, photosNeedingReplacement, rectInBounds } from '../data/guide/monsterPhotoSheets'
import { extraMonsterPhotos, extraMonsterSheets, EXTRA_SHEET_SIZE, extraPhotoCount, extraRectInBounds } from '../data/guide/monsterExtraPhotoSheets'
import { npcSheets } from '../data/guide/npcPhotoSheets'
import { loadedPhotoIndex, photoIndexNote } from '../data/guide/monsterPhotoIndexNotes'
import { contrastPhotoIndex, formatContrast } from './photoIndexContrast'
import { canonicalNpcId, npcIdentities } from '../data/guide/npcIdentity'
import { resolveImageEntries } from './entityImages'
import { classificationConflicts, classifyEntity, classificationLabel, type EntityClassification } from './monsterKind'
import type { WorldData } from '../types/world'

/*
 * Diagnóstico ÚNICO de imágenes de entidades de 06_monsters (monstruos, NPC, objetos y pendientes de clasificación).
 * - Alias del staff (Pet Trainer 234 → Trainer 226) aplicados ANTES de contar.
 * - Identidad en cuatro comprobaciones separadas: rótulo leído · contrastada con el índice original ·
 *   inspección visual · apariencia confirmada en Vanguard. Leer el rótulo no cubre las otras tres.
 * - Encuadre: ejemplares superpuestos (reemplazo recomendado) y rótulo del juego visible (mejora de presentación).
 * - Cargas medidas por URL en este navegador, incluidas las fotos del staff.
 */

export type SheetLoad = { status: 'cargando' | 'ok' | 'error'; w?: number; h?: number }
export type PackKey = 'inicial' | 'extra'
export type EntityClass = EntityClassification

export const PACKS: { key: PackKey; name: string; sheets: Record<number, string>; size: number; reference: { ids: number; photos?: number; sheets: number } }[] = [
  { key: 'inicial', name: 'VANGUARD_MOBS_LIMPIOS', sheets: monsterSheets, size: MONSTER_SHEET_SIZE, reference: { ids: 154, sheets: 10 } },
  { key: 'extra', name: 'VANGUARD_MONSTRUOS_EXTRA', sheets: extraMonsterSheets, size: EXTRA_SHEET_SIZE, reference: { ids: 131, photos: 39, sheets: 3 } },
]

/** IDs que el staff pidió revisar expresamente. */
export const AUDIT_CHECK_IDS = [313, 420, 315, 362, 363, 460, 461, 462, 407, 226]

/** Encuadre: varios ejemplares en la misma foto (conviene reemplazarla). */
export const OVERLAP_IDS = [14, 62, 308, 354, 425, 570]
/** Encuadre: el rótulo del juego aparece en la captura (mejora de presentación, no error de identidad). */
export const GAME_LABEL_IDS = [365, 463]

export const sheetKey = (pack: PackKey, sheet: number) => `${pack}:${sheet}`

type PackEntry = { pack: PackKey; id: number; sheet: number; src: string; sourceName: string; inBounds: boolean; manualFocus: boolean; sharedWith: number[]; replace?: string }

const replaceReason = new Map(photosNeedingReplacement.map((p) => [p.id, p.reason]))

const entries: PackEntry[] = [
  ...monsterPhotos.map((p) => ({ pack: 'inicial' as const, id: p.id, sheet: p.sheet, src: monsterSheets[p.sheet], sourceName: p.sourceName, inBounds: rectInBounds(p), manualFocus: !!photoFocus[p.id], sharedWith: [], replace: replaceReason.get(p.id) })),
  ...extraMonsterPhotos.map((p) => ({ pack: 'extra' as const, id: p.id, sheet: p.sheet, src: extraMonsterSheets[p.sheet], sourceName: p.sourceName, inBounds: extraRectInBounds(p), manualFocus: p.focus.zoom !== 1 || p.focus.fx !== 0.5 || p.focus.fy !== 0.5, sharedWith: p.sharedWith })),
]

/** Todas las URL que se pueden medir: láminas de los tres paquetes. Las fotos del staff se añaden desde el catálogo. */
export const ALL_SHEET_URLS = [...Object.values(monsterSheets), ...Object.values(extraMonsterSheets), ...Object.values(npcSheets)]

type Origin = 'staff' | PackKey | 'npc'

function imageOrigin(key: string | undefined, origin: string | undefined): Origin | undefined {
  if (!key) return undefined
  if (key.startsWith('paquete-extra:')) return 'extra'
  if (key.startsWith('paquete-npc:')) return 'npc'
  if (key.startsWith('paquete:')) return 'inicial'
  return origin ? 'staff' : undefined
}

const originLabel: Record<Origin, string> = { staff: 'foto del staff', inicial: 'LIMPIOS', extra: 'EXTRA', npc: 'láminas NPC' }

const norm = (s: string) => s.toLowerCase().replace(/\(.*?\)/g, '').replace(/[^a-z0-9]/g, '')

const mergedIds = new Set(npcIdentities.filter((n) => n.mergedInto !== undefined).map((n) => n.id))

export function entityClass(w: WorldData, id: number): EntityClass {
  const m = w.monsters.get(canonicalNpcId(id))
  return m ? classifyEntity(w, m).cls : 'pendiente'
}

export type IdentityStages = {
  labelRead: string
  indexContrasted: string
  visualInspection: string
  vanguardAppearance: string
}

export type AuditRow = {
  pack: PackKey
  id: number
  sourceName: string
  sheet: number
  catalogName?: string
  cls?: EntityClass
  identity: 'coincide' | 'denominación descriptiva documentada' | 'nombre distinto' | 'no está en 06_monsters'
  stages: IdentityStages
  familyReference: string
  load: SheetLoad['status'] | 'sin comprobar'
  framing: 'correcto' | 'fuera de su celda' | 'ejemplares superpuestos' | 'rótulo del juego visible'
  framingNote?: string
  assigned: 'este paquete' | 'foto del staff (conservada)' | 'paquete inicial (conservada)' | 'láminas NPC' | 'sin asignar'
  visible: 'sí' | 'no' | 'sin comprobar'
  sharedWith: number[]
}

export type NeededImage = { id: number; name: string; cls: EntityClass; reason: string; priority: number }

export function buildMonsterPhotoAudit(w: WorldData, loads: Record<string, SheetLoad>, run: boolean) {
  const { byMonster } = resolveImageEntries(w)
  const loadOf = (src?: string): SheetLoad['status'] | 'sin comprobar' => (!run || !src ? 'sin comprobar' : loads[src]?.status ?? 'cargando')

  const index = loadedPhotoIndex()
  const contrast = index ? contrastPhotoIndex(w, Array.from(index.values())) : undefined
  const contrastById = contrast ? new Map(contrast.rows.map((r) => [r.id, r])) : undefined

  const rows: AuditRow[] = entries.map((e) => {
    const m = w.monsters.get(e.id)
    const entry = byMonster.get(e.id)
    const img = entry?.image
    const note = photoIndexNote(e.id)
    const nameMatches = !!m && (norm(m.name) === norm(e.sourceName) || norm(m.name).startsWith(norm(e.sourceName)) || norm(e.sourceName).startsWith(norm(m.name)))
    const identity: AuditRow['identity'] = !m ? 'no está en 06_monsters' : nameMatches ? 'coincide' : note?.mappedName ? 'denominación descriptiva documentada' : 'nombre distinto'
    const load = loadOf(e.src)
    const origin = imageOrigin(entry?.key, img?.origin)
    const assigned: AuditRow['assigned'] = !m || !img ? 'sin asignar' : origin === 'staff' ? 'foto del staff (conservada)' : origin === e.pack ? 'este paquete' : origin === 'inicial' ? 'paquete inicial (conservada)' : 'láminas NPC'
    const framing: AuditRow['framing'] = !e.inBounds ? 'fuera de su celda' : OVERLAP_IDS.includes(e.id) ? 'ejemplares superpuestos' : GAME_LABEL_IDS.includes(e.id) ? 'rótulo del juego visible' : 'correcto'
    const visible: AuditRow['visible'] = assigned !== 'este paquete' ? 'no' : !run ? 'sin comprobar' : load === 'ok' && e.inBounds ? 'sí' : 'no'
    return {
      pack: e.pack,
      id: e.id,
      sourceName: e.sourceName,
      sheet: e.sheet,
      catalogName: m?.name,
      cls: m ? classifyEntity(w, m).cls : undefined,
      identity,
      stages: {
        labelRead: `sí — ID ${e.id} impreso bajo la foto («${e.sourceName}»)`,
        indexContrasted: (() => {
          const c = contrastById?.get(e.id)
          if (c) return c.status === 'coincide' ? `sí — ID, lámina y recorte coinciden (${c.detail})` : `no — ${c.status} (${c.detail})`
          return note
            ? `no — datos del índice transcritos por el staff, sin lámina ni recorte${note.sourceName ? `: source_name «${note.sourceName}»` : ''}${note.mappedName ? ` → «${note.mappedName}»` : ''}`
            : 'no — el índice original (JSON) no está cargado'
        })(),
        visualInspection: `${e.replace || e.manualFocus ? 'no registrada ahora (solo se revisó el encuadre)' : 'no registrada ahora'}${note?.indexVisuallyChecked !== undefined ? ` · antecedente del índice: visually_checked=${note.indexVisuallyChecked}` : ''}`,
        vanguardAppearance: 'no confirmada',
      },
      familyReference: note?.familyReference ? 'sí (family_reference=true en el índice original)' : e.sharedWith.length ? `foto compartida con ${e.sharedWith.join(', ')} (leída de la lámina)` : 'no',
      load,
      framing,
      framingNote: [e.replace, note?.warning, e.manualFocus ? 'encuadre manual por ID' : ''].filter(Boolean).join(' · ') || undefined,
      assigned,
      visible,
      sharedWith: e.sharedWith,
    }
  })

  const packs = PACKS.map((p) => {
    const pr = rows.filter((r) => r.pack === p.key)
    const ids = new Set(pr.map((r) => r.id))
    const photos = p.key === 'extra' ? extraPhotoCount : ids.size
    const sheetIds = Object.keys(p.sheets).map(Number)
    const sl = sheetIds.map((n) => ({ n, s: loads[p.sheets[n]] }))
    return {
      ...p,
      rows: pr,
      received: { sheets: sheetIds.length, ids: ids.size, photos },
      loaded: {
        ok: sl.filter((l) => l.s?.status === 'ok').length,
        failed: sl.filter((l) => l.s?.status === 'error').map((l) => l.n),
        resized: sl.filter((l) => l.s?.status === 'ok' && (l.s.w !== p.size || l.s.h !== p.size)).map((l) => `${l.n}: ${l.s!.w}×${l.s!.h}`),
      },
      assigned: {
        thisPack: pr.filter((r) => r.assigned === 'este paquete').length,
        keptStaff: pr.filter((r) => r.assigned === 'foto del staff (conservada)').length,
        keptOther: pr.filter((r) => r.assigned === 'paquete inicial (conservada)' || r.assigned === 'láminas NPC').length,
        notInCatalog: pr.filter((r) => r.identity === 'no está en 06_monsters').length,
      },
      visible: run ? pr.filter((r) => r.visible === 'sí').length : undefined,
      identityIssues: pr.filter((r) => r.identity === 'nombre distinto' || r.identity === 'no está en 06_monsters'),
      descriptive: pr.filter((r) => r.identity === 'denominación descriptiva documentada'),
      loadIssues: pr.filter((r) => r.load === 'error'),
    }
  })

  const unionIds = new Set(rows.map((r) => r.id))
  const overlap = Array.from(new Set(rows.filter((r) => r.pack === 'extra').map((r) => r.id))).filter((id) => rows.some((r) => r.pack === 'inicial' && r.id === id))

  // ---- Catálogo: original → publicado único (alias aplicados) ----
  const all = Array.from(w.monsters.values()).sort((a, b) => a.id - b.id)
  const aliasRows = all.filter((m) => mergedIds.has(m.id)).map((m) => ({ id: m.id, name: m.name, into: canonicalNpcId(m.id), intoName: w.monsters.get(canonicalNpcId(m.id))?.name }))
  const published = all.filter((m) => !mergedIds.has(m.id))
  const cls = new Map(published.map((m) => [m.id, classifyEntity(w, m)]))

  type Assigned = { id: number; name: string; cls: EntityClass; origin: Origin; src: string; load: SheetLoad['status'] | 'sin comprobar'; needsConfirmation: boolean }
  const assignedList: Assigned[] = published.flatMap((m) => {
    const entry = byMonster.get(m.id)
    const img = entry?.image
    const origin = imageOrigin(entry?.key, img?.origin)
    if (!img || !origin) return []
    return [{ id: m.id, name: m.name, cls: cls.get(m.id)!.cls, origin, src: img.src, load: loadOf(img.src), needsConfirmation: !!img.needsConfirmation }]
  })
  const staffPhotos = assignedList.filter((a) => a.origin === 'staff')
  const withoutImage = published.filter((m) => !byMonster.get(m.id)?.image)

  const CLASSES: EntityClass[] = ['monstruo', 'npc', 'objeto', 'pendiente']
  const byClass = (c: EntityClass) => {
    const list = published.filter((m) => cls.get(m.id)!.cls === c)
    return {
      total: list.length,
      withImage: assignedList.filter((a) => a.cls === c).length,
      pending: withoutImage.filter((m) => cls.get(m.id)!.cls === c).map((m) => ({ id: m.id, name: m.name, cls: c, evidence: cls.get(m.id)!.evidence })),
    }
  }

  // ---- Lista depurada de imágenes realmente necesarias ----
  const needed: NeededImage[] = []
  withoutImage.forEach((m) => {
    const c = cls.get(m.id)!
    if (c.cls === 'monstruo') needed.push({ id: m.id, name: m.name, cls: c.cls, priority: 1, reason: `Monstruo publicado sin foto (${c.evidence})` })
    else if (c.cls === 'npc') needed.push({ id: m.id, name: m.name, cls: c.cls, priority: 2, reason: 'NPC publicado sin foto' })
    else if (c.cls === 'objeto') needed.push({ id: m.id, name: m.name, cls: c.cls, priority: 3, reason: 'Objeto publicado sin foto' })
    else needed.push({ id: m.id, name: m.name, cls: c.cls, priority: 4, reason: `Sin foto. Antes de pedirla, clasificar la entidad: ${c.evidence}` })
  })
  assignedList.forEach((a) => {
    if (a.origin === 'inicial' && OVERLAP_IDS.includes(a.id)) needed.push({ id: a.id, name: a.name, cls: a.cls, priority: 5, reason: `Reemplazo recomendado: ejemplares superpuestos (${replaceReason.get(a.id) ?? ''}). Se conserva la actual hasta tener reemplazo.` })
    if (a.origin === 'inicial' && GAME_LABEL_IDS.includes(a.id)) needed.push({ id: a.id, name: a.name, cls: a.cls, priority: 6, reason: 'Mejora de presentación: rótulo del juego visible. No es un error de identidad; se conserva la actual.' })
    if (a.load === 'error') needed.push({ id: a.id, name: a.name, cls: a.cls, priority: 0, reason: `La imagen asignada (${originLabel[a.origin]}) no cargó en este navegador` })
  })
  needed.sort((a, b) => a.priority - b.priority || a.id - b.id)

  const checks = AUDIT_CHECK_IDS.map((id) => {
    const m = w.monsters.get(id)
    const inPacks = rows.filter((r) => r.id === id)
    const entry = byMonster.get(id)
    const img = entry?.image
    const origin = imageOrigin(entry?.key, img?.origin)
    const assignedLoad = loadOf(img?.src)
    return {
      id,
      name: m?.name,
      cls: m ? classifyEntity(w, m).cls : undefined,
      received: inPacks.length ? inPacks.map((r) => `${r.pack === 'inicial' ? 'LIMPIOS' : 'EXTRA'} lámina ${r.sheet} («${r.sourceName}»)`).join(' + ') : 'no está en los paquetes de monstruos',
      stages: inPacks[0]?.stages,
      familyReference: inPacks[0]?.familyReference ?? '—',
      framing: inPacks.map((r) => `${r.framing}${r.framingNote ? ` (${r.framingNote})` : ''}`).join(', ') || '—',
      assigned: !img || !origin ? 'sin foto (marcador neutro)' : `${originLabel[origin]}${img.needsConfirmation ? ' · asociación pendiente de confirmar por el staff' : ''}`,
      load: !img ? '—' : assignedLoad,
    }
  })

  const assignedLoads = {
    ok: assignedList.filter((a) => a.load === 'ok').length,
    failed: assignedList.filter((a) => a.load === 'error'),
  }

  return {
    rows,
    packs,
    unionIds: unionIds.size,
    overlap,
    catalogTotal: all.length,
    aliasRows,
    publishedTotal: published.length,
    classes: CLASSES,
    byClass,
    assignedList,
    staffPhotos,
    assignedLoads,
    withImage: assignedList.length,
    pending: withoutImage.map((m) => ({ id: m.id, name: m.name, cls: cls.get(m.id)!.cls })),
    needed,
    indexContrasted: Array.from(new Set(rows.filter((r) => r.stages.indexContrasted.startsWith('sí')).map((r) => r.id))),
    contrast,
    conflicts: classificationConflicts(w).filter((c) => !mergedIds.has(c.id)),
    checks,
  }
}

export type MonsterPhotoAudit = ReturnType<typeof buildMonsterPhotoAudit>

export const clsLabel = classificationLabel

export function formatNeededImages(a: MonsterPhotoAudit): string {
  return [
    `IMÁGENES NECESARIAS — entidades publicadas (${a.publishedTotal} únicas, alias aplicados): ${a.needed.length} líneas`,
    'Fotos del staff conservadas. Ninguna entidad se oculta por faltarle imagen.',
    'ID\tNombre\tTipo\tMotivo',
    ...a.needed.map((n) => `${n.id}\t${n.name}\t${clsLabel[n.cls]}\t${n.reason}`),
  ].join('\n')
}

export function formatMonsterPhotoAudit(a: MonsterPhotoAudit, run: boolean): string {
  const st = (s?: IdentityStages) => (s ? `\n  rótulo: ${s.labelRead}\n  índice original: ${s.indexContrasted}\n  inspección visual: ${s.visualInspection}\n  apariencia en Vanguard: ${s.vanguardAppearance}` : '\n  identidad: no está en los paquetes de monstruos')
  const L: string[] = [
    'DIAGNÓSTICO DE IMÁGENES DE ENTIDADES (06_monsters)',
    `Calculado: ${new Date().toLocaleString('es-ES')} en este navegador. ${run ? '' : 'Cargas SIN comprobar: pulsa «Comprobar cargas».'}`,
    a.contrast
      ? 'Índice original cargado en este navegador. Contrastado = coinciden ID, lámina y recorte.'
      : 'Índice original NO cargado: los IDs se leyeron de los rótulos y ninguno cuenta como contrastado.',
    ...(a.contrast ? ['', formatContrast(a.contrast)] : []),
    '',
    '## 1. CATÁLOGO',
    `Catálogo original (06_monsters): ${a.catalogTotal} IDs`,
    `Alias aplicados: ${a.aliasRows.map((r) => `${r.id} ${r.name} → ${r.into} ${r.intoName ?? ''}`).join('; ') || 'ninguno'} (registro original conservado; no se cuenta aparte ni como foto pendiente)`,
    `Entidades publicadas únicas: ${a.publishedTotal}`,
    ...a.classes.map((c) => {
      const b = a.byClass(c)
      return `  ${clsLabel[c]}: ${b.total} · con imagen ${b.withImage} · sin imagen ${b.pending.length}`
    }),
    'Prioridad: administración → función explícita (misiones, invasiones documentadas) → lámina NPC (sin función acreditada) → evidencia combinada (2+ fuentes). Una sola ubicación, spawn o recompensa no basta. Nunca por nombre, ID, nivel ni foto. La exportación no trae tipo de entidad: 0 objetos automáticos.',
    `Contradicciones para revisión: ${a.conflicts.length}`,
    ...a.conflicts.map((c) => `  ! ${c.id}\t${c.name}\t${clsLabel[c.cls]}\t${c.conflict}`),
    '',
    '## 2. IMÁGENES ASIGNADAS',
    `Total: ${a.withImage} · staff ${a.staffPhotos.length} · LIMPIOS ${a.assignedList.filter((x) => x.origin === 'inicial').length} · EXTRA ${a.assignedList.filter((x) => x.origin === 'extra').length} · láminas NPC ${a.assignedList.filter((x) => x.origin === 'npc').length}`,
    '',
    '## 3. CARGAS COMPROBADAS',
    run ? `Imágenes asignadas que cargaron: ${a.assignedLoads.ok}/${a.withImage}` : 'Sin comprobar.',
    ...a.packs.map((p) => `${p.name}: ${run ? `${p.loaded.ok}/${p.received.sheets} láminas${p.loaded.failed.length ? ` · no cargan: ${p.loaded.failed.join(', ')}` : ''}${p.loaded.resized.length ? ` · otro tamaño: ${p.loaded.resized.join('; ')} (recorte escalado)` : ''}` : 'sin comprobar'}`),
    `Fotos del staff (${a.staffPhotos.length}):`,
    ...a.staffPhotos.map((s) => `  - ${s.id}\t${s.name}\t${run ? s.load : 'sin comprobar'}${s.needsConfirmation ? '\tasociación pendiente de confirmar' : ''}`),
    ...(run ? a.assignedLoads.failed.map((f) => `  ✗ ${f.id}\t${f.name}\t${originLabel[f.origin]} no cargó`) : []),
    '',
    '## 4. IDENTIDADES REVISADAS',
    `Rótulo leído: ${a.rows.length} filas de paquete (${a.unionIds} IDs distintos; referencia 285)${a.overlap.length ? ` · en ambos paquetes: ${a.overlap.join(', ')}` : ''}`,
    `Contrastadas con el índice original (ID + lámina + recorte): ${a.indexContrasted.length}`,
    'Inspección visual hecha ahora: 0 (visually_checked del índice se informa aparte como antecedente)',
    'Apariencia confirmada en Vanguard: 0',
    ...a.packs.flatMap((p) => [
      `${p.name}: ${p.received.ids} IDs · ${p.received.photos} fotos (referencia ${p.reference.ids} IDs${p.reference.photos ? ` · ${p.reference.photos} fotos` : ''}) · usan esta foto ${p.assigned.thisPack} · staff conservadas ${p.assigned.keptStaff} · otra lámina ${p.assigned.keptOther}`,
      ...p.descriptive.map((r) => `  · ${r.id}\tlámina «${r.sourceName}» / catálogo «${r.catalogName}»\tdenominación descriptiva documentada`),
      ...p.identityIssues.map((r) => `  ! ${r.id}\tlámina «${r.sourceName}»\t${r.identity}${r.catalogName ? `: catálogo «${r.catalogName}»` : ''}`),
    ]),
    'Fotos compartidas (EXTRA: varias IDs por foto): cada ID conserva su ficha, nivel, mapas, estadísticas y drops.',
    '',
    '## 5. ENCUADRE',
    `Ejemplares superpuestos (reemplazo recomendado): ${OVERLAP_IDS.join(', ')}`,
    `Rótulo del juego visible (mejora de presentación, no error de identidad): ${GAME_LABEL_IDS.join(', ')}`,
    'Se conservan las imágenes y encuadres aprobados hasta disponer de un reemplazo.',
    ...a.rows.filter((r) => r.framing === 'fuera de su celda').map((r) => `  ! ${r.id}\t${r.catalogName ?? r.sourceName}\tfuera de su celda`),
    '',
    '## 6. CASOS PEDIDOS',
    ...a.checks.map((c) => `${c.id}\t${c.name ?? 'no existe en 06_monsters'}\t${c.cls ? clsLabel[c.cls] : '—'}\n  recibido: ${c.received}${st(c.stages)}\n  referencia de familia: ${c.familyReference}\n  encuadre: ${c.framing}\n  asignado: ${c.assigned}\n  carga de la imagen asignada: ${run ? c.load : 'sin comprobar'}`),
    '',
    '## 7. ' + formatNeededImages(a),
  ]
  return L.join('\n')
}
